import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { AiOutlineDown } from "react-icons/ai";
import logo from "@/assets/nav_logo.png";
import "./index.less";

const NewNav = () => {
  const location = useLocation();
  const pathname = location?.pathname;
  const [activeDropdown, setActiveDropdown] = useState(-1);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [closingDropdown, setClosingDropdown] = useState(-1);

  const isHomePage = pathname === "/";

  // 滚动隐藏逻辑
  useEffect(() => {
    let ticking = false;

    const updateNavVisibility = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > 10 && !hasScrolled) {
        setHasScrolled(true);
      }

      if (isHomePage && currentScrollY <= 10) {
        setHasScrolled(false);
      }

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateNavVisibility);
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY, hasScrolled, isHomePage]);

  // 重置状态当路由变化
  useEffect(() => {
    const currentScrollY = window.scrollY;

    if (isHomePage && currentScrollY <= 10) {
      setHasScrolled(false);
      setIsVisible(true);
    } else {
      setHasScrolled(true);
      setIsVisible(true);
    }
    setActiveDropdown(-1);
    setClosingDropdown(-1);
  }, [pathname, isHomePage]);

  // 服务案例下拉数据
  const caseItems = useMemo(
    () => [
      { series: "GPU服务器运维方案", id: "operations" },
      { series: "GPU服务器改配方案", id: "reconfiguration" },
      { series: "GPU服务器搬迁方案", id: "removal" },
    ],
    []
  );
  
  // 解决方案下拉数据
  const solutionItems = useMemo(
    () => [
      { series: "交付解决方案", id: "delivery" },
      { series: "运维解决方案", id: "devOps" },
      { series: "维修解决方案", id: "repair" },
    ],
    []
  );

  const navItems = useMemo(
    () => [
      { path: "/", key: "/", name: "首页" },
      { path: "/product", key: "product", name: "产品服务" },
      {
        path: "/solution",
        key: "solution",
        name: "解决方案",
        hasDropdown: true,
        dropdownData: solutionItems,
        dropdownBasePath: "/solution", // 新增：指定基础路径
      },
      {
        path: "/case",
        key: "case",
        name: "服务案例",
        hasDropdown: true,
        dropdownData: caseItems,
        dropdownBasePath: "/case", // 新增：指定基础路径
      },
      { path: "/about", key: "about", name: "关于我们" },
    ],
    [caseItems, solutionItems]
  );

  const isActiveLink = useCallback(
    (navItem) => {
      if (pathname === "/") return navItem.key === "/";
      if (navItem.hasDropdown) return pathname.startsWith(navItem.path);
      return pathname === navItem.path;
    },
    [pathname]
  );

  const handleDropdownClick = useCallback(
    (e, index) => {
      e.preventDefault();

      if (activeDropdown === index) {
        setClosingDropdown(index);
        setTimeout(() => {
          setActiveDropdown(-1);
          setClosingDropdown(-1);
        }, 300);
      } else {
        setActiveDropdown(index);
        setClosingDropdown(-1);
      }
    },
    [activeDropdown]
  );

  const handleSubItemClick = useCallback(() => {
    setClosingDropdown(activeDropdown);
    setTimeout(() => {
      setActiveDropdown(-1);
      setClosingDropdown(-1);
    }, 300);
  }, [activeDropdown]);

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        activeDropdown !== -1 &&
        !e.target.closest(".nav_item_content") &&
        !e.target.closest(".dropdown-menu-container")
      ) {
        setClosingDropdown(activeDropdown);
        setTimeout(() => {
          setActiveDropdown(-1);
          setClosingDropdown(-1);
        }, 300);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  // 构建类名
  const navClassNames = [
    "new_nav_page",
    isVisible ? "nav-visible" : "nav-hidden",
    isHomePage ? "home-page" : "other-page",
    hasScrolled ? "scrolled" : "not-scrolled",
    activeDropdown !== -1 ? "dropdown-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={navClassNames}>
      <div className="nav_warp">
        <div className="left-flex">
          <div className="logo">
            <Link to="/">
              <img src={logo} alt="Logo" className="logo-image" />
            </Link>
          </div>

          <div className="nav_list">
            {navItems.map((item, index) => {
              const isActive = isActiveLink(item);
              const isDropdownOpen = activeDropdown === index;
              const isClosing = closingDropdown === index;

              return (
                <div className="nav_item" key={item.key}>
                  <div className="nav_item_content">
                    <Link
                      to={item.path}
                      className={isActive ? "active" : ""}
                      onClick={
                        item.hasDropdown
                          ? (e) => handleDropdownClick(e, index)
                          : undefined
                      }
                    >
                      {item.name}
                      {item.hasDropdown && (
                        <AiOutlineDown
                          className={`icon ${isDropdownOpen ? "rotated" : ""}`}
                        />
                      )}
                    </Link>

                    {/* 下拉菜单 */}
                    {item.hasDropdown && isDropdownOpen && (
                      <div
                        className={`dropdown-menu-container ${
                          isClosing ? "closing" : ""
                        }`}
                      >
                        <div className="dropdown-menu">
                          <div className="jump-item">
                            {item.dropdownData.map((dropdownItem) => (
                              <Link
                                key={dropdownItem.id}
                                to={`${item.dropdownBasePath}/${dropdownItem.id}`}
                                className="dropdown-item"
                                onClick={handleSubItemClick}
                              >
                                {dropdownItem.series}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewNav;